import { useState } from 'react';
import type { NotableUnit } from '@/lib/types/division-profile';
import type { Unit } from '@/lib/types/unit';
import { useDataStore } from '@/lib/store/data-store';
import { getUnitImagePath } from '@/lib/utils/unit-images';
import { UnitInfoDialog } from '@/components/DeckBuilder/UnitInfoDialog';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';

interface KpiWithUnitsProps {
  label: string;
  value: string | number;
  units?: NotableUnit[];
}

export function KpiWithUnits({
  label,
  value,
  units,
}: KpiWithUnitsProps) {
  const { getUnitByDescriptor } = useDataStore();
  const [selectedUnit, setSelectedUnit] = useState<Unit | undefined>();
  const [dialogOpen, setDialogOpen] = useState(false);

  function handleClick(descriptorName: string) {
    const unit = getUnitByDescriptor(descriptorName);
    if (unit) {
      setSelectedUnit(unit);
      setDialogOpen(true);
    }
  }

  const hasUnits = units && units.length > 0;
  const disabled = !hasUnits && (value === 0 || value === '0' || value === '—');

  return (
    <div className={cn(
      "flex flex-col items-center rounded-lg border bg-muted/30 px-2 py-1.5 flex-1 min-w-[88px] gap-1",
      disabled && "opacity-35"
    )}>
      <div className="flex flex-col items-center">
        <span className="text-sm font-bold tabular-nums leading-none text-foreground">
          {value}
        </span>
        <span className="text-[10px] text-muted-foreground mt-0.5 text-center leading-tight">
          {label}
        </span>
      </div>

      {hasUnits && (
        <div className="flex flex-wrap gap-1 justify-center mt-0.5">
          {units.map((u, i) => (
            <Tooltip key={`${u.descriptorName}-${i}`}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => handleClick(u.descriptorName)}
                  className="relative w-6 h-6 rounded border bg-muted/50 overflow-hidden hover:ring-1 hover:ring-primary transition-shadow cursor-pointer"
                >
                  <img
                    src={getUnitImagePath(u.descriptorName)}
                    alt={u.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  {u.cards > 1 && (
                    <span className="absolute -bottom-0.5 -right-0.5 min-w-3 h-3 flex items-center justify-center rounded-full bg-foreground text-background text-[8px] font-bold leading-none px-0.5">
                      {u.cards}
                    </span>
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                {u.name}{u.cards > 1 ? ` ×${u.cards}` : ''}
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      )}

      {selectedUnit && (
        <UnitInfoDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          unit={selectedUnit}
        />
      )}
    </div>
  );
}
